import { AlertSummary, ALERT_SEVERITY_COLORS } from '../../types/operational';

interface AlertsListProps {
  alerts: AlertSummary[];
  onAlertClick?: (alert: AlertSummary) => void;
}

export function AlertsList({ alerts, onAlertClick }: AlertsListProps) {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
      critical: 'bg-red-200 text-red-900'
    };
    const labels = {
      low: 'Faible',
      medium: 'Moyen',
      high: 'Élevé',
      critical: 'Critique'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[severity as keyof typeof colors]}`}>
        {labels[severity as keyof typeof labels]}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'bg-red-100 text-red-800',
      acknowledged: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800'
    };
    const labels = {
      active: 'Active',
      acknowledged: 'Acquittée',
      resolved: 'Résolue'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Alertes Actives</h3>
        <span className="text-sm text-gray-500">{alerts.length} alerte(s)</span>
      </div>
      <div className="space-y-3">
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">✅</div>
            <p className="text-gray-500">Aucune alerte active</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div 
              key={alert.id} 
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onAlertClick?.(alert)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {getSeverityBadge(alert.severity)}
                  {getStatusBadge(alert.status)}
                </div>
                <span className="text-xs text-gray-500">{formatTime(alert.timestamp)}</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-1">{alert.description}</h4>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{alert.hotelName}</span>
                {alert.guestName && (
                  <span className="font-medium">👤 {alert.guestName}</span>
                )}
              </div>
              <div 
                className="w-full h-1 mt-3 rounded-full"
                style={{ backgroundColor: ALERT_SEVERITY_COLORS[alert.severity] }}
              ></div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
